import { Radar, Brain, Send, LineChart, ShieldCheck, Zap } from "lucide-react";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";

const PILLARS = [
  {
    icon: Radar,
    stage: "Stage 01",
    title: "Signal Detection",
    desc: "Continuously ingest 15+ live signal types across intent, tech-install, funding, hiring, and content engagement.",
    metric: "15+ signals",
  },
  {
    icon: Brain,
    stage: "Stage 02",
    title: "Intent Scoring",
    desc: "AI models score every account across five intent sources into one consolidated priority index — no manual triage.",
    metric: "5 sources · 1 score",
  },
  {
    icon: Send,
    stage: "Stage 03",
    title: "Multi-Channel Activation",
    desc: "LinkedIn, programmatic, email, tele, and events — orchestrated by an AI planner that adapts spend in near real-time.",
    metric: "5 channels · 1 planner",
  },
  {
    icon: ShieldCheck,
    stage: "Stage 04",
    title: "Real-Time Qualification",
    desc: "BANT-checked, dual-employment-checked, dedup'd against your CRM — before the lead hits your sales team.",
    metric: "18 checks · 4 gates",
  },
  {
    icon: Zap,
    stage: "Stage 05",
    title: "Live Handover",
    desc: "Native CRM delivery into Salesforce, HubSpot, and Monday. Signal + reason + next-best-action attached.",
    metric: "Salesforce · HubSpot · APIs",
  },
  {
    icon: LineChart,
    stage: "Stage 06",
    title: "Continuous Attribution",
    desc: "Closed-loop learning re-scores your ICP weekly. Cooling accounts drop, warming ones rise. Automatically.",
    metric: "Weekly re-score",
  },
];

export default function Framework() {
  return (
    <section
      id="framework"
      data-testid="framework-section"
      className="relative py-24 md:py-32 border-t border-white/5 bg-[#040810] overflow-hidden"
    >
      {/* Traveling signal line at top of section */}
      <div aria-hidden className="absolute left-0 right-0 top-0 h-px overflow-hidden">
        <div className="signal-line" />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            AI-Native Lead Gen Framework
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
            Six stages.{" "}
            <span className="italic font-normal text-teal">One AI-native pipeline.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            An operating model designed from the first mile of signal detection to
            the last mile of pipeline attribution — no vendor stitching, no
            spreadsheet handovers, no black-boxes.
          </p>
        </Reveal>

        <div className="mt-14 relative">
          {/* Decorative dashed connectors behind the grid (large screens only) */}
          <div aria-hidden className="pointer-events-none hidden lg:block absolute inset-0">
            <svg width="100%" height="100%" viewBox="0 0 1200 460" preserveAspectRatio="none">
              <defs>
                <linearGradient id="conn" x1="0" x2="1">
                  <stop offset="0" stopColor="#00E5C2" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#00E5C2" stopOpacity="0.35" />
                  <stop offset="1" stopColor="#00E5C2" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M 60 90 L 1140 90"
                stroke="url(#conn)"
                strokeDasharray="4 8"
                strokeWidth="1"
                fill="none"
                className="draw-line"
              />
              <path
                d="M 60 350 L 1140 350"
                stroke="url(#conn)"
                strokeDasharray="4 8"
                strokeWidth="1"
                fill="none"
                className="draw-line"
                style={{ animationDelay: "0.3s" }}
              />
            </svg>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 110} y={30}>
                  <div
                    data-testid={`framework-stage-${i + 1}`}
                    className="group relative rounded-2xl border border-white/[0.06] bg-[#0A121A] p-7 card-hover overflow-hidden h-full"
                  >
                    {/* traveling dot along top edge on hover */}
                    <span className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-teal/60 to-transparent stage-shimmer" style={{ animationDelay: `${i * 0.4}s` }} />

                    <div className="flex items-center justify-between mb-8">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-lg bg-teal/10 border border-teal/20 grid place-items-center text-teal">
                          <Icon size={18} />
                        </div>
                        {/* pulse ring behind icon */}
                        <span className="absolute inset-0 rounded-lg border border-teal/40 stage-pulse" style={{ animationDelay: `${i * 0.35}s` }} />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                        {p.stage}
                      </div>
                    </div>
                    <h3 className="font-display text-2xl font-semibold mb-3 leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {p.desc}
                    </p>
                    <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-teal">
                        {p.metric}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground">
                        {String(i + 1).padStart(2, "0")} / 06
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Bottom summary bar */}
        <Reveal delay={300}>
          <div className="mt-10 rounded-2xl border border-teal/25 bg-teal/[0.03] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 relative overflow-hidden">
            <span className="absolute inset-y-0 left-0 w-1 bg-teal/60 summary-glow" />
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-teal pulse-dot" />
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-teal">Live production</div>
            </div>
            <div className="text-sm md:text-base text-foreground/90">
              End-to-end SLAs from brief to booked meeting — one accountable partner,
              no black boxes, no vendor stitching.
            </div>
            <div className="md:ml-auto flex items-center gap-8">
              <div>
                <div className="font-display text-2xl font-semibold">
                  <CountUp end={1.8} decimals={1} suffix="s" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Per lead</div>
              </div>
              <div>
                <div className="font-display text-2xl font-semibold">
                  <CountUp end={98} suffix="%" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Accuracy</div>
              </div>
              <div>
                <div className="font-display text-2xl font-semibold">
                  <CountUp end={94.2} decimals={1} suffix="%" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">AI confidence</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <style>{`
        @keyframes signalTravel {
          0% { transform: translateX(-30%); }
          100% { transform: translateX(130%); }
        }
        .signal-line {
          height: 1px;
          width: 30%;
          background: linear-gradient(90deg, transparent, #00E5C2, transparent);
          animation: signalTravel 6s linear infinite;
          box-shadow: 0 0 12px rgba(0,229,194,0.6);
        }

        @keyframes stagePulse {
          0% { transform: scale(1); opacity: 0.7; }
          80% { opacity: 0.05; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .stage-pulse { animation: stagePulse 3.6s ease-out infinite; }

        @keyframes shimmer {
          0% { opacity: 0; transform: translateX(-100%); }
          40% { opacity: 1; }
          100% { opacity: 0; transform: translateX(100%); }
        }
        .stage-shimmer { animation: shimmer 5s ease-in-out infinite; }

        @keyframes drawIn {
          from { stroke-dashoffset: 800; opacity: 0; }
          to { stroke-dashoffset: 0; opacity: 1; }
        }
        .draw-line {
          stroke-dashoffset: 800;
          animation: drawIn 2.6s ease-out forwards;
        }

        @keyframes glowSlide {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; box-shadow: 0 0 16px rgba(0,229,194,0.5); }
        }
        .summary-glow { animation: glowSlide 3s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
